import { useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
} from "antd";
import dayjs from "dayjs";
import styles from "../../deliveryServices/DeliveryServices.module.css";
import { businessType } from "../../../constants/typeOfEntity";
import useStore from "../../../hooks/useStore";
import getSensitiveData from "../../apiRepository/getSensitiveData";
import getStoreDetails from "../../apiRepository/getStoreDetails";
import postOnboardForm from "../../apiRepository/postOnboardForm";
import GrubhubDialogConfirm from "./GrubHubDialogConfirm";
import GrubhubDialogCancel from "./GrubhubDialogCancel";

const dateFormatList = ["MM/DD/YYYY", "MM/DD/YY", "MM-DD-YYYY", "MM-DD-YY"];

export default function GrubHubOnBoardingForm({ closeForm, handleNotification }) {
  const { store, setStore } = useStore();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [apiErrors, setApiErrors] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [hasFormError, setHasFormError] = useState(false);

  const handleCloseModal = () => {
    setShowCancelModal(false);
    setShowConfirmModal(false);
    setApiErrors(null);
    setIsSubmitting(false);
  };

  const handlePostOnboarding = async () => {
    const data = { ...formValues };
    data.sensitiveData.legalDOB = dayjs(data.sensitiveData?.legalDOB).format("MM/DD/YYYY");
    data.sensitiveData.ein = String(data.sensitiveData.ein).replace(/-/g, "");
    delete data.isExisting;

    const res = await postOnboardForm({
      token: store?.token,
      integratorId: store?.integratorId,
      storeId: store?.storeId,
      deliveryService: "GRUBHUB",
      isExisting: false,
      data,
    });

    return res;
  };

  const submit = async () => {
    try {
      setIsSubmitting(true);
      setApiErrors(null);

      // Send api request and redirect to generated link
      const res = await handlePostOnboarding();
      window.open(res.data.link);

      // Update local storage
      localStorage.setItem(
        store.storeId,
        JSON.stringify({
          ...store.onboardingFormSubmitted,
          GRUBHUB: true,
        }),
      );

      // Update store state
      const storeDetails = await getStoreDetails({
        integratorId: store?.integratorId,
        storeId: store.storeId,
        token: store?.token,
      });
      const formData = await getSensitiveData({
        integratorId: store?.integratorId,
        storeId: store.storeId,
        token: store?.token,
      }).catch((err) => console.error(err));

      setStore((prev) => ({
        ...prev,
        ...storeDetails,
        onboardingFormSubmitted: {
          ...prev.onboardingFormSubmitted,
          GRUBHUB: true,
        },
        formData,
      }));

      setIsSubmitting(false);
      closeForm();
      handleNotification();
    } catch (err) {
      setIsSubmitting(false);
      const { errors } = err?.response?.data || {};
      console.log(err?.response || err.message);

      setApiErrors(errors || err.message);
    }
  };

  const hanldeConfirm = (data) => {
    setFormValues(data);
    setShowConfirmModal(true);
  };

  return (
    <>
      <div style={{ padding: "30px 80px" }}>
        <div style={{ marginBottom: "30px" }}>
          <p style={{ fontWeight: 700, marginBottom: "5px" }}>
            Connect to Grubhub
          </p>
          <p className={styles.dsFormTitle2}>
            Fill out form with your details as the store contact person.
            Processing may take up to 24 hours or longer.
          </p>
        </div>
        <Form
          autoComplete="off"
          layout="vertical"
          onFinish={hanldeConfirm}
          onFinishFailed={() => setHasFormError(true)}
          initialValues={{
            firstName: store.formData?.contactFirstName,
            lastName: store.formData?.contactLastName,
            salesTax:
              store?.taxRate || store?.salesTax || store?.formData?.salesTax,
            sensitiveData: {
              routingNumber: store?.formData?.routingNumber,
              bankAccountNumber: store?.formData?.bankAccountNumber,
              ein: store?.formData?.ein,
              ssn: store?.formData?.ssn,
              legalBusinessName: store?.formData?.legalBusinessName,
              legalDOB: dayjs(store?.formData?.legalDOB),
              legalEmail: store?.formData?.legalEmail || store?.email,
              typeOfEntity: store?.formData?.typeOfEntity,
            },
          }}
        >
          <Row gutter={30}>
            <Col span={12}>
              <Form.Item
                label={<span className={styles.dsFormLabels}>First Name</span>}
                name="firstName"
                required
                rules={[{ required: true, message: "First name is required." }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label={<span className={styles.dsFormLabels}>Email</span>}
                name={["sensitiveData", "legalEmail"]}
                required
                rules={[
                  { required: true, message: "Email is required." },
                  { type: "email", message: "Invalid email." },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span className={styles.dsFormLabels}>Last Name</span>}
                name="lastName"
                required
                rules={[{ required: true, message: "Last name is required." }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <p className={styles.dsFormText}>
                The fields below are required by Grubhub to streamline your
                store's onboarding experience
              </p>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>
                    Legal Business Name
                  </span>
                }
                name={["sensitiveData", "legalBusinessName"]}
                required
                rules={[
                  { required: true, message: "Business Name is required." },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col span={8}>
              <Form.Item
                label={<span className={styles.dsFormLabels}>Entity Type</span>}
                name={["sensitiveData", "typeOfEntity"]}
                required
                rules={[
                  { required: true, message: "Entity Type is required." },
                ]}
              >
                <Select options={businessType} placeholder="Please select" />
              </Form.Item>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Account Number</span>
                }
                name={["sensitiveData", "bankAccountNumber"]}
                required
                rules={[
                  { required: true, message: "Account Number is required." },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Routing Number</span>
                }
                name={["sensitiveData", "routingNumber"]}
                required
                rules={[
                  { required: true, message: "Routing Number is required." },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Date of Birth</span>
                }
                name={["sensitiveData", "legalDOB"]}
                required
                rules={[
                  { required: true, message: "Date of birth is required." },
                ]}
              >
                <DatePicker
                  initialValues={dayjs("01/01/2015", dateFormatList[0])}
                  format={dateFormatList}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>
                    Employer ID Number
                  </span>
                }
                name={["sensitiveData", "ein"]}
                required
                rules={[
                  {
                    required: true,
                    message: "EIN is required.",
                  },
                ]}
              >
                <Input maxLength={10} placeholder="123456789" />
              </Form.Item>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>
                    Last 4 Digits of Owner's SSN
                  </span>
                }
                name={["sensitiveData", "ssn"]}
                required
                rules={[
                  {
                    required: true,
                    message: "Social Security Number is required.",
                  },
                  {
                    pattern: /^\d{4}$/,
                    message: "SSN must be 4 digits.",
                  },
                ]}
              >
                <Input maxLength={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col span={8}>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Store Tax (%)</span>
                }
                name="salesTax"
                required
                rules={[
                  { required: true, message: "This is a required field." },
                  {
                    validator: (_, value) => {
                      if (value && +value > 100) {
                        return Promise.reject(
                          "Please enter a value that is 100 or less.",
                        );
                      }

                      if (value && +value <= 0) {
                        return Promise.reject(
                          "Please enter a value that is greater than 0",
                        );
                      }

                      if (value && String(value).trim() === ".") {
                        return Promise.reject(
                          "Please enter a value that is greater than 0",
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                normalize={(value) => {
                  if (!value) return value;
                  const cleaned = value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*?)\..*/g, "$1");
                  let truncated = cleaned.slice(0, cleaned.indexOf(".") + 4);
                  return truncated;
                }}
              >
                <Input suffix={"%"} maxLength={6} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="space-between">
            <Col>
              <Button
                type="default"
                danger
                onClick={() => setShowCancelModal(true)}
                className={styles.dsButtonCancel}
              >
                <p>Cancel</p>
              </Button>
            </Col>
            <Col
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 20,
              }}
            >
              {hasFormError ? (
                <p className={styles.dsFormErrorText}>
                  Please check the fields marked in red and try again.
                </p>
              ) : (
                ""
              )}
              <Button htmlType="submit" className={styles.dsButtonSubmit}>
                <p>Proceed</p>
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
      <GrubhubDialogConfirm
        handleSubmit={submit}
        handleCloseModal={handleCloseModal}
        isSubmitting={isSubmitting}
        showModal={showConfirmModal}
        errors={apiErrors}
        clearError={() => setApiErrors(null)}
      />
      <GrubhubDialogCancel
        closeForm={closeForm}
        handleCloseModal={handleCloseModal}
        showModal={showCancelModal}
      />
    </>
  );
}